import _ from 'lodash';
import {
  DetailedHTMLProps,
  AnchorHTMLAttributes,
  CSSProperties,
  FC,
  ReactNode,
  createElement,
  MouseEvent,
  useMemo,
  ElementType
} from 'react';
import { Link as ReactRouterLink } from 'react-router-dom';
import { withSiteMeta } from '../HOCS/withSiteMeta';
import i18n from 'i18next';
import { setPathLocale } from '../../common/localeShared';
import { onlyTextChildren } from '../libs/reactOnlyTextChildren';
import { LocationDescriptor } from 'history';
import { getTrackedInternalLinkUrl } from '../libs/tracking';

type TTrackedLinkProps = DetailedHTMLProps<AnchorHTMLAttributes<HTMLAnchorElement>, HTMLAnchorElement> & {
  as?: string;
  children?: ReactNode;
};

export function TrackedLink(props: TTrackedLinkProps) {
  let { href } = props;

  if (href?.startsWith('/')) {
    // make non-absolute URLs absolute
    href = RIO_SITE.external_url + href;
  }

  const newProps: TTrackedLinkProps = {
    ...props,
    rel: props.rel || 'noopener',
    onClick: props.onClick
      ? props.onClick
      : (ev: any) => {
          ev.stopPropagation();
          const trackFn = (window as any).trackOutboundLink;
          if (trackFn) {
            ev.preventDefault();
            trackFn(href, props.as);
          }
          return true;
        }
  };
  delete newProps.as;
  return <a {...newProps}>{props.children}</a>;
}

// TODO properly type this
export type TLinkProps = {
  to: string | LocationDescriptor;
  as?: string;
  target?: string;
  title?: string;
  className?: string;
  style?: CSSProperties;
  tabIndex?: number;
  onClick?: (ev: MouseEvent<HTMLAnchorElement>) => void;
  siteConfig?: any;
  children?: ReactNode;
};

const BaseLink: FC<TLinkProps> = (props) => {
  const { to, children, siteConfig, ...otherProps } = props;

  const [editedToObject, editedToString] = useMemo<[string | LocationDescriptor, string]>(() => {
    if (typeof to === 'string') {
      const path = setPathLocale(to || '', i18n.language);
      return [path, path];
    }

    const pathname = to.pathname || '';
    const search = to.search || '';
    const hash = to.hash || '';

    const toObject = {
      pathname: setPathLocale(pathname, i18n.language),
      search,
      hash
    };

    return [toObject, `${pathname}${search}${hash}`];
  }, [to]);

  if (siteConfig.isEmbedded) {
    return (
      <TrackedLink {...otherProps} href={editedToString}>
        {children}
      </TrackedLink>
    );
  } else {
    const onClick = (ev: any) => {
      RIO_TRACK_EVENT('event', 'internal_click', {
        link_url: getTrackedInternalLinkUrl(editedToString),
        link_text: onlyTextChildren(children),
        link_classes: otherProps.className || '',
        link_id: otherProps.as || ''
      });
      return otherProps.onClick?.(ev);
    };

    return createElement(ReactRouterLink as any, { ...otherProps, to: editedToObject, onClick }, children);
  }
};

export const Link: typeof BaseLink = withSiteMeta(BaseLink, ['siteConfig']) as unknown as any;

export function PossibleLink(props: any) {
  const { allowLink, to, className, children, ...otherProps } = props;
  if (allowLink) {
    return (
      <Link to={to} className={className} {...otherProps}>
        {children}
      </Link>
    );
  } else {
    return (
      <span className={className} {...otherProps}>
        {children}
      </span>
    );
  }
}

export function OnsiteOrOffsiteLink(props: any) {
  const isExternal = props.to.indexOf('://') !== -1;
  const componentProps = _.omit(props, ['to', 'children']);
  if (isExternal) {
    componentProps.href = props.to;
    return (
      <TrackedLink href={props.to} {...componentProps}>
        {props.children}
      </TrackedLink>
    );
  } else {
    return (
      <Link to={props.to} {...componentProps}>
        {props.children}
      </Link>
    );
  }
}

interface IConditionalLinkProps extends Omit<TLinkProps, 'to'> {
  children: ReactNode;
  to?: string | null;
  fallbackComponent?: ElementType;
}

export const ConditionalLink = ({
  children,
  to,
  fallbackComponent: FallbackComponent,
  target,
  ...props
}: IConditionalLinkProps) => {
  if (to) {
    return (
      <Link to={to} target={target} {...props}>
        {children}
      </Link>
    );
  }

  const Component = FallbackComponent || 'span';

  return <Component {...props}>{children}</Component>;
};
